import { Component, DestroyRef, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterPillComponent } from 'lib';
import { NgHeaderTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { OrganizationStatus } from '../../enums/organization-status-enum';
import { DaysInOrganizationFilter } from '../../enums/days-in-organization-filter-enum';
import { MigrationDateFilter } from '../../enums/migration-date-filter-enum';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgIf } from '@angular/common';
import { OrganizationSiteFilterDto } from '../../interfaces/organization-site-filter-dto';
import { PodSearchService } from '../../services/pod-search.service';
import { catchError, debounceTime, distinctUntilChanged, filter, Observable, of, Subject, switchMap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

const organizationStatusLabels: Record<OrganizationStatus, string> = {
    [OrganizationStatus.ACTIVE]: 'Active',
    [OrganizationStatus.INACTIVE]: 'Inactive',
    [OrganizationStatus.CONFIGURING]: 'Configuring'
};

@Component({
    selector: 'mmoga-organization-site-filter',
    standalone: true,
    imports: [
        FilterPillComponent,
        NgSelectComponent,
        NgHeaderTemplateDirective,
        FormsModule,
        NgIf,
        AsyncPipe
    ],
    templateUrl: './organization-site-filter.component.html',
    styleUrl: './organization-site-filter.component.scss'
})
export class OrganizationSiteFilterComponent implements OnInit {
    private readonly podSearchService = inject(PodSearchService);
    private readonly toastrService = inject(ToastrService);


    @Output() filterRequestEvent = new EventEmitter<OrganizationSiteFilterDto>();

    protected readonly organizationStatusOpts = Object.values(OrganizationStatus).map(
        (value) => ({
            label: organizationStatusLabels[value],
            value
        })
    );
    protected readonly belongsToOrganizationOpts = [
        { label: 'Part of an organization', value: true },
        { label: 'Not part of an organization', value: false }
    ];
    protected readonly autoMigrateOpts = [
        { label: 'Auto-migrate', value: false },
        { label: 'Do not auto-migrate', value: true }
    ];
    protected readonly daysInOrganizationOpts = [
        { label: 'Less than 15 days', value: DaysInOrganizationFilter.LESS_THAN },
        { label: 'Between 15-30 days', value: DaysInOrganizationFilter.BETWEEN },
        { label: 'More than 30 days', value: DaysInOrganizationFilter.MORE_THAN }
    ];
    // Shared options for both preconfig and enablement date filters — same thresholds, same labels
    protected readonly migrationDateFilterOpts = [
        { label: 'Less than 15 days from today', value: MigrationDateFilter.LESS_THAN },
        { label: 'Between 15-30 days from today', value: MigrationDateFilter.BETWEEN },
        { label: 'More than 30 days from today', value: MigrationDateFilter.MORE_THAN }
    ];

    selectedOrganizationStatusOpt: OrganizationStatus | null = null;
    selectedBelongsToOrganizationOpt: boolean | null = null;
    selectedAutoMigrateOpt: boolean | null = null;
    selectedPodOpt: string | null = null;
    selectedDaysInOrganizationOpt: DaysInOrganizationFilter | null = null;
    selectedPreconfigDateFilterOpt: MigrationDateFilter | null = null;
    selectedEnableDateFilterOpt: MigrationDateFilter | null = null;

    pods$!: Observable<string[]>;
    showFilters = false;

    @ViewChild('podSearchInput') podSearchInput!: ElementRef;

    private readonly podSearch$ = new Subject<string>();
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.pods$ = this.podSearch$.pipe(
            takeUntilDestroyed(this.destroyRef),
            debounceTime(300),
            distinctUntilChanged(),
            filter(term => !term || term.length >= 3),
            switchMap(term => this.podSearchService.searchPods(term).pipe(
                catchError(() => {
                    this.toastrService.error('Failed to load pods.');
                    return of([]);
                })
            ))
        );
    }

    onPodSearchInput(event: Event): void {
        const searchTerm = (event.target as HTMLInputElement).value;
        this.podSearch$.next(searchTerm);
    }

    focusPodSearchInput(): void {
        setTimeout(() => {
            if (this.podSearchInput) {
                this.podSearchInput.nativeElement.focus();
                this.podSearch$.next('');
            }
        });
    }

    applyFilters(): void {
        this.emitFilterRequest();
    }

    clearFilters(): void {
        this.selectedOrganizationStatusOpt = null;
        this.selectedBelongsToOrganizationOpt = null;
        this.selectedAutoMigrateOpt = null;
        this.selectedPodOpt = null;
        this.selectedDaysInOrganizationOpt = null;
        this.selectedPreconfigDateFilterOpt = null;
        this.selectedEnableDateFilterOpt = null;
        this.emitFilterRequest();
    }

    private emitFilterRequest(): void {
        this.filterRequestEvent.emit({
            status: this.selectedOrganizationStatusOpt,
            belongsToEnterprise: this.selectedBelongsToOrganizationOpt,
            doNotEnable: this.selectedAutoMigrateOpt,
            pod: this.selectedPodOpt,
            daysInOrganizationFilter: this.selectedDaysInOrganizationOpt,
            preconfigDateFilter: this.selectedPreconfigDateFilterOpt,
            enableDateFilter: this.selectedEnableDateFilterOpt
        } as OrganizationSiteFilterDto);
    }
}
